import type { RecommendationDataProvider, ProductCardItem } from '../types';
import type { RecommendedItemsParams, RecommendedProviderContext } from './types';

/**
 * Утилита для получения уникальных элементов в порядке появления
 */
function uniqueInOrder<T>(items: T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = typeof item === 'string' ? item : JSON.stringify(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}

/**
 * Тип для записи о просмотре товара
 */
type RecentEntry = {
  t?: number;
  p?: string;
  c?: string[];
};

/**
 * Парсит данные из cookie recent_views
 */
function parseRecentViews(raw: string | undefined): RecentEntry[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((e) => e && typeof e === 'object')
        .slice(0, 20);
    }
  } catch (_) {
    // Игнорируем ошибки парсинга
  }

  return [];
}

/**
 * Извлекает листовые категории из записей просмотров
 */
function extractLeafSections(entries: RecentEntry[]): string[] {
  return uniqueInOrder(
    entries
      .map((e) => Array.isArray(e.c) ? e.c.filter(Boolean) : [])
      .map((chain) => chain.length > 0 ? chain[chain.length - 1] : undefined)
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
  ).slice(0, 5);
}

/**
 * Извлекает код последнего просмотренного товара
 */
function extractLastViewedProduct(entries: RecentEntry[]): string | undefined {
  const lastViewedProduct = entries.find((e) => typeof e.p === 'string' && e.p.trim());
  return lastViewedProduct?.p;
}

/**
 * Создает провайдер для рекомендованных товаров на основе истории просмотров
 * @param fetchRecommendedProducts - Функция для получения рекомендованных товаров из проекта
 * @returns Провайдер данных для рекомендованных товаров
 */
export function createRecommendedProvider(
  fetchRecommendedProducts: (params: RecommendedItemsParams) => Promise<ProductCardItem[]>
): RecommendationDataProvider<RecommendedItemsParams, RecommendedProviderContext> {
  return {
    /**
     * Получает рекомендованные товары на основе истории просмотров
     */
    async fetch(params: RecommendedItemsParams): Promise<ProductCardItem[]> {
      return await fetchRecommendedProducts(params);
    },

    /**
     * Возвращает параметры по умолчанию на основе контекста (cookies)
     */
    getDefaultParams(context?: RecommendedProviderContext): RecommendedItemsParams | null {
      // Парсим данные из cookie
      const entries = parseRecentViews(context?.recentViews);

      // Если нет записей, возвращаем null (провайдер не должен делать запрос)
      if (entries.length === 0) {
        return null;
      }

      // Извлекаем листовые категории
      const leafSections = extractLeafSections(entries);

      // Если нет категорий, возвращаем null
      if (leafSections.length === 0) {
        return null;
      }

      // Извлекаем код последнего просмотренного товара для исключения
      const excludeCode = extractLastViewedProduct(entries);

      return {
        sectionCodes: leafSections,
        excludeCode,
        limit: 10,
      };
    },
  };
}

